import { useEffect, useState } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { fr } from "date-fns/locale";
import { api, fmtDuration } from "@/lib/api";

const when = (iso) => {
  const d = new Date(iso);
  const day = isToday(d) ? "aujourd'hui" : isYesterday(d) ? "hier" : format(d, "dd/MM", { locale: fr });
  return `${day} @ ${format(d, "HH:mm:ss")}`;
};
const span = (a, b) => fmtDuration(Math.max(0, Math.round(((b ? new Date(b) : new Date()) - new Date(a)) / 1000)));

const Row = ({ at, children, dot }) => (
  <li className="relative pl-6 py-1.5 text-[13px]">
    <span className={`absolute left-[3px] top-3 h-2.5 w-2.5 rounded-full ${dot}`} />
    <p className="text-slate-400 text-[11px]">{when(at)}</p>
    {children}
  </li>
);

// Agent online periods (green line) with the technician sessions and commands that happened in each.
export const Timeline = ({ id }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/machines/${id}/timeline`).then(({ data }) => setD(data)).catch(() => setD({ periods: [], sessions: [], commands: [] }));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [id]);
  if (!d) return <p className="text-sm text-slate-500">Chargement…</p>;
  const items = [
    ...d.periods.flatMap((p) => [{ at: p.end || new Date().toISOString(), kind: "end", p }, { at: p.start, kind: "start", p }]),
    ...d.sessions.map((s) => ({ at: s.started_at, kind: "session", s })),
    ...d.commands.map((c) => ({ at: c.at, kind: "cmd", c })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="machine-timeline-empty">Aucune connexion enregistrée sur les 14 derniers jours.</p>;
  return (
    <ul className="relative border-l-2 border-green-200 ml-2" data-testid="machine-timeline">
      {items.map((it, i) => {
        if (it.kind === "end") return (
          <Row key={i} at={it.at} dot={it.p.end ? "bg-slate-400" : "bg-green-500 animate-pulse"}>
            <p className="font-semibold text-slate-800">{it.p.end ? "Hors ligne" : "En ligne maintenant"} <span className="ml-1 rounded-full bg-slate-600 text-white text-[11px] px-2 py-0.5">{span(it.p.start, it.p.end)}</span></p>
          </Row>
        );
        if (it.kind === "start") return (
          <Row key={i} at={it.at} dot="bg-green-500"><p className="font-semibold text-slate-800">Agent connecté{d.user ? ` · ${d.user}` : ""}</p></Row>
        );
        if (it.kind === "session") return (
          <Row key={i} at={it.at} dot="bg-blue-600">
            <p className="text-slate-800"><b>{it.s.user_name}</b> a pris la main · {it.s.ended_at ? fmtDuration(it.s.duration_s) : "en cours"}</p>
          </Row>
        );
        return (
          <Row key={i} at={it.at} dot="bg-slate-300">
            <p className="text-slate-700">{it.c.user_name} : <code className="text-[12px] bg-slate-100 px-1 rounded break-all">{it.c.command.slice(0, 120)}</code></p>
          </Row>
        );
      })}
    </ul>
  );
};
